import React, { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { PencilSimple, Trash } from '@phosphor-icons/react';

import Button from 'partial/components/Button';
import ThumbIcon from 'partial/components/ThumbIcon';
import { useCreateModal } from 'partial/components/Modal';
import { aclHelper } from 'helper';
import AddUpdateRole from '../modals/AddUpdateRole';

import DeleteRole from '../modals/DeleteRole';
import Permissions from './Permissions';

import { useRoleById } from '../hooks';

function RoleDetails({ roles }) {
  const { id } = useParams();
  const createModal = useCreateModal();
  const [isLoading, roleDetails] = useRoleById(id);

  const roleName = roleDetails?.name;

  const handleEditRole = useCallback(() => {
    createModal({
      content: (onClose) => (
        <AddUpdateRole type="edit" prevData={roleDetails} onClose={onClose} />
      ),
    });
  }, [createModal, roleDetails]);

  const handleDeleteRole = useCallback(() => {
    createModal({
      content: (onClose) => (
        <DeleteRole prevData={roleDetails} roles={roles} onClose={onClose} />
      ),
    });
  }, [createModal, roleDetails, roles]);

  return (
    <div className="flex-grow min-w-0">
      {isLoading ? (
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-neutral-100 h-24 w-24" />
          <div className="flex-1 space-y-6 py-1">
            <div className="h-8 bg-neutral-100 rounded" />
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="h-8 bg-neutral-100 rounded col-span-2" />
                <div className="h-8 bg-neutral-100 rounded col-span-1" />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-6 flex items-start justify-between gap-6 border-b border-neutral-200">
          <figure className="flex items-center gap-4 min-w-0">
            <ThumbIcon
              className="h-[52px] w-[52px] rounded-full"
              initialsClassName="!text-lg"
              alt={roleName}
            />
            <figcaption className="min-w-0">
              <h2 className="text-xl font-bold capitalize text-neutral-900 truncate">
                {roleName || '--'}
              </h2>
              <p className="text-sm text-neutral-500 truncate">
                {roleDetails?.code?.replace(/_/g, ' ') || '--'}
              </p>
            </figcaption>
          </figure>
          <div className="flex gap-2 items-center flex-shrink-0">
            {aclHelper(
              'role',
              'u',
              <Button
                outline
                icon={PencilSimple}
                disabled={isLoading}
                onClick={handleEditRole}
              >
                Edit
              </Button>,
            )}

            {aclHelper(
              'role',
              'd',
              <Button
                outline
                icon={Trash}
                disabled={isLoading}
                onClick={handleDeleteRole}
              >
                Delete
              </Button>,
            )}
          </div>
        </div>
      )}

      <Permissions roleId={id} />
    </div>
  );
}

RoleDetails.propTypes = {
  roles: PropTypes.instanceOf(Array).isRequired,
};

export default memo(RoleDetails);
